import { useEffect, useRef } from "react";
import { updateAlertLocation } from "../lib/api";
import { enqueueLocationRefinement, flushLocationRefinementQueue } from "../lib/locationRefinementQueue";

// Iter90 — GPS audit: an SOS alert is sent IMMEDIATELY with whatever
// fix `useGeolocation` has at that instant (never blocked waiting for
// an "ideal" GPS lock). This hook keeps riding the SAME watchPosition
// stream for a short window afterward and pushes any STRICTLY BETTER
// fix onto the SAME alert(s) — it never creates a new alert.
const REFINEMENT_WINDOW_MS = 60_000;
// Throttle outgoing PATCH calls so a jittery GPS stream (many samples
// per second on some Android devices) can't flood the backend.
const MIN_SEND_GAP_MS = 3_000;
// Iter91 — backstop flush interval for the offline retry queue. The
// `online` browser event is the primary trigger; this timer catches
// environments where that event is unreliable (some mobile browsers /
// flaky captive-portal Wi-Fi that never fires a clean transition).
const QUEUE_FLUSH_INTERVAL_MS = 15_000;

/**
 * const { arm } = useLocationRefinement(coords);
 * ...
 * const alert = await createAlert(payload);
 * arm([alert.id], payload.coordinates.accuracy);
 *
 * `arm()` resets the window and target ids — safe to call again for a
 * fresh SOS press, or with several ids at once for a multi-type SOS.
 */
export function useLocationRefinement(coords) {
  const alertIdsRef = useRef([]);
  const deadlineRef = useRef(0);
  const bestSentAccuracyRef = useRef(Infinity);
  const lastSendRef = useRef(0);

  const arm = (alertIds, initialAccuracy) => {
    alertIdsRef.current = Array.isArray(alertIds) ? alertIds.filter(Boolean) : [];
    deadlineRef.current = Date.now() + REFINEMENT_WINDOW_MS;
    bestSentAccuracyRef.current = initialAccuracy ?? Infinity;
    lastSendRef.current = 0;
  };

  useEffect(() => {
    if (!coords || alertIdsRef.current.length === 0) return;
    if (Date.now() > deadlineRef.current) return;
    // Prefer the rolling best-fix (same source of truth the SOS button
    // itself reads) over the bare latest sample.
    const fix = coords.best || coords;
    if (fix.accuracy == null || fix.accuracy >= bestSentAccuracyRef.current) return;
    const now = Date.now();
    if (now - lastSendRef.current < MIN_SEND_GAP_MS) return;
    lastSendRef.current = now;
    const ids = alertIdsRef.current;
    const candidateAccuracy = fix.accuracy;
    const deadline = deadlineRef.current;
    ids.forEach((id) => {
      updateAlertLocation(id, fix)
        .then(() => {
          // Only advance the "best sent" watermark on confirmed
          // success — a failed/offline PATCH must not block a later
          // retry of the same (or a better) fix within the window.
          bestSentAccuracyRef.current = Math.min(bestSentAccuracyRef.current, candidateAccuracy);
        })
        .catch(() => {
          // Iter91 — offline or transiently failing: queue this fix
          // (localStorage-backed) instead of dropping it. Retried on
          // 'online', on next mount, and by the periodic backstop
          // below — never by creating a second alert.
          enqueueLocationRefinement(id, fix, deadline);
        });
    });
  }, [coords]);

  // Iter91 — offline retry queue: flush on mount (covers a fix queued
  // right before the tab was closed/backgrounded), on the browser's
  // 'online' transition, and on a periodic backstop timer for the
  // remainder of this component's lifetime.
  useEffect(() => {
    const sendOne = (id, fix) => updateAlertLocation(id, fix);
    const flush = () => { flushLocationRefinementQueue(sendOne); };
    flush();
    window.addEventListener("online", flush);
    const timer = window.setInterval(flush, QUEUE_FLUSH_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", flush);
      window.clearInterval(timer);
    };
  }, []);

  return { arm };
}
